import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Plus, RotateCcw } from "lucide-react";
import { useW } from "./ctx";
import { SourceTag } from "./Popover";
import { FLAG, lineStatus } from "./lib";

const LETTERS = "abcdefghijklmnop";

export function Checklist() {
  const w = useW();
  const [open, setOpen] = useState(false);
  const c = w.data.checklist;
  const inc = c.filter((x) => x.status === "included").length;
  const building = w.project.run.status !== "done";
  return (
    <div className="card mb-5 overflow-hidden" data-testid="checklist">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold">What the submittal must include</p>
          <p className="text-[12.5px] text-[var(--muted)]">Section {w.project.spec_section} submittal schema · items a–h</p>
        </div>
        <span className="badge b-met tnum" data-testid="checklist-count">
          {building ? "Building" : `${inc} of ${c.length} included${c.length - inc ? ` · ${c.length - inc} partial` : ""}`}</span>
        <button className="btn btn-sm" onClick={() => setOpen(!open)} data-testid="checklist-toggle">{open ? "Hide" : "Show"}</button>
      </div>
      {open && (
        <table className="grid text-[13px]">
          <thead><tr><th className="w-[36px]" /><th>Requirement</th><th className="w-[30%]">Where it lives in the submittal</th><th className="w-[96px]">Status</th><th className="w-[104px]" /></tr></thead>
          <tbody>
            {c.map((x, i) => (
              <tr key={x.key}>
                <td className="mono text-[var(--muted)]">{x.letter || LETTERS[i]}.</td>
                <td>{x.text}{x.clauses?.length ? <span className="text-[var(--muted)]"> · Spec {x.clauses.join(", ")}</span> : null}</td>
                <td className="text-[var(--ink2)]">{x.where}{x.note ? <span className="block text-[12px] text-[var(--muted)]">{x.note}</span> : null}</td>
                <td><span className={`badge ${x.status === "included" ? "b-resolved" : "b-confirm"}`}>{x.status === "included" ? "Included" : "Partial"}</span></td>
                <td><button className="btn btn-ghost btn-sm" onClick={() => w.seeInDoc(x.key)} data-testid={`see-in-doc-${x.key}`}>See in doc</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Decisions still open on a door, for whoever is looking: the supplier decides, then the architect reviews.
const openOn = (w, door) => w.data.findings.filter((f) => f.line && f.door === door
  && !(w.project.decisions?.[f.id] || {})[w.role === "supplier" ? "supplier" : "architect"]).length;

export function SubmittalQuick() {
  const w = useW();
  const [filter, setFilter] = useState("all");
  const { doors, lines } = w.data;
  const byDoor = useMemo(() => {
    const m = {};
    lines.forEach((l) => (m[l.door] = m[l.door] || []).push(l));
    return m;
  }, [lines]);
  const flagged = new Set(w.data.findings.filter((f) => f.line).map((f) => f.door));
  const openDoors = new Set([...flagged].filter((d) => openOn(w, d) > 0));
  const shown = doors.slice(0, w.revealDoors).filter((d) => filter === "all" || (filter === "decide" ? openDoors.has(d.door) : !flagged.has(d.door)));
  return (
    <div className="mx-auto max-w-[1100px] px-6 py-5">
      <Checklist />
      <div className="mb-3 flex items-center gap-2">
        {[["all", `All doors (${doors.length})`], ["decide", `Needs a decision (${openDoors.size})`], ["met", "Nothing to decide"]].map(([k, l]) => (
          <button key={k} className={`btn btn-sm ${filter === k ? "btn-dark" : ""}`} onClick={() => setFilter(k)} data-testid={`filter-${k}`}>{l}</button>
        ))}
      </div>
      {filter === "decide" && openDoors.size === 0 && <p className="card px-4 py-6 text-center text-[var(--ink2)]">Every decision is made.</p>}
      <div className="space-y-2">
        {shown.map((d) => <Door key={d.door} d={d} lines={byDoor[d.door] || []} flagged={flagged.has(d.door)} open_={openOn(w, d.door)} />)}
      </div>
      {w.revealDoors < doors.length && <p className="mt-4 flex items-center gap-2 text-[var(--muted)]"><span className="dot-live" /> Adding line items…</p>}
    </div>
  );
}

function Door({ d, lines, flagged, open_ }) {
  const w = useW();
  const open = w.openDoors[d.door] ?? flagged;
  const adds = (w.project.arch_adds || []).filter((a) => a.door === d.door);
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const [showMet, setShowMet] = useState(!!w.settings?.showMetLines);
  // On a door with a flag, only the flagged lines show; the rest are one click away.
  const met = flagged ? lines.filter((l) => !l.flag && !(w.focusLine === l.id)) : [];
  const shown = flagged && !showMet ? lines.filter((l) => !met.includes(l)) : lines;
  return (
    <div className="card fade-up overflow-hidden" data-door={d.door}>
      <button className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-[var(--bg)]" onClick={() => w.toggleDoor(d.door, !open)}
        data-testid={`door-${d.door}`}>
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        <span className="mono font-semibold text-[var(--ink)]">{d.door}</span>
        <span className="font-medium">{d.room}</span>
        <span className="text-[12.5px] text-[var(--ink2)]">{d.set} · {d.rating_min ? `${d.rating_min} min` : "unrated"}{d.stc ? ` · STC ${d.stc}` : ""} · {d.hand}
          {d.access_control ? " · access controlled" : ""}</span>
        <span className="ml-auto flex items-center gap-2">
          {open_ > 0 && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-[var(--accent)] px-1.5 text-[11px] font-semibold text-white"
            title={`${open_} unresolved`} data-testid={`door-open-${d.door}`}>{open_}</span>}
          <span className="text-[12.5px] text-[var(--muted)]">{d.plan_ref}</span>
        </span>
        <span className={`badge ${!flagged ? "b-met" : open_ ? "b-confirm" : "b-resolved"}`} data-testid={`door-status-${d.door}`}>
          {!flagged ? `${lines.length} lines · all met` : open_ ? "Decision" : "Resolved"}</span>
      </button>
      {open && (
        <div className="divider">
          <div className="grid grid-cols-[84px_40px_minmax(0,1fr)_minmax(0,1.6fr)_120px] gap-3 bg-[var(--panel)] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[.06em] text-[var(--muted)]">
            <span>Line</span><span>Qty</span><span>Item</span><span>Sources</span><span className="text-right">Status</span>
          </div>
          {shown.map((l) => <Line key={l.id} l={l} />)}
          {met.length > 0 && (
            <button className="w-full border-t border-[var(--line)] px-4 py-1.5 text-left text-[12.5px] text-[var(--ink2)] hover:bg-[var(--bg)]"
              onClick={() => setShowMet(!showMet)} data-testid={`show-met-${d.door}`}>
              {showMet ? "Hide met lines" : `Show ${met.length} met line${met.length > 1 ? "s" : ""}`}</button>
          )}
          {adds.map((a) => (
            <div key={a.id} className="grid grid-cols-[84px_40px_1fr_auto] items-center gap-3 border-t border-[var(--line)] bg-[var(--blue-bg)] px-4 py-2 text-[13px]">
              <span className="mono">{a.id}</span><span>1</span><span>{a.text}</span><span className="badge b-edited">Added by architect</span>
            </div>
          ))}
          {w.role === "architect" && !w.project.approved_at && (
            <div className="border-t border-[var(--line)] px-4 py-2">
              {adding ? (
                <div className="flex gap-2">
                  <input autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="For example: kick plate, 10 in, push side"
                    className="h-8 flex-1 rounded-md border border-[var(--line2)] px-2 outline-none" data-testid={`add-line-input-${d.door}`} />
                  <button className="btn btn-sm btn-dark" disabled={!text.trim()} onClick={async () => { await w.addLine(d.door, text); setText(""); setAdding(false); }}>Add</button>
                  <button className="btn btn-sm" onClick={() => setAdding(false)}>Cancel</button>
                </div>
              ) : <button className="btn btn-ghost btn-sm" onClick={() => setAdding(true)} data-testid={`add-line-${d.door}`}><Plus size={13} /> Add a line</button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const TAG_ORDER = (id) => (id.startsWith("A601-") ? 0 : id.startsWith("OS-") ? 1 : id.startsWith("SEC-") ? 2 : /^HW-/.test(id) ? 3 : /^S\d/.test(id) ? 4 : /^C\d/.test(id) ? 5 : 6);

// What is actually on the line: a chat edit, or the alternative the supplier chose, else Condoor's product.
export function lineProduct(w, l) {
  const f = l.finding ? w.findingById[l.finding] : null;
  const chat = (w.project.edits || []).find((e) => e.line === l.id);
  const alt = !chat && f?.alt_cat && w.project.decisions?.[f.id]?.supplier === "alt"
    ? { to: f.alt_cat, product: `${f.alt_cat}, ${f.kind === "substitution" ? "special order" : "as named"}` } : null;
  return { f, chat, ed: chat || alt };
}

function Line({ l }) {
  const w = useW();
  const { f, chat } = lineProduct(w, l);
  const st = chat ? { label: `Edited by ${chat.by}`, cls: "b-edited" } : lineStatus(l, f, w.project.decisions);
  const tags = [...l.src].filter((s) => !s.includes("#")).sort((a, b) => TAG_ORDER(a) - TAG_ORDER(b));
  const focused = w.focusLine === l.id;
  // Only the item's name shows here; the product, catalog number and price are in the popover a click away.
  return (
    <div data-line={l.id} className={`border-t border-[var(--line)] ${focused ? "outline-pulse" : ""} ${f && l.flag ? "bg-[#fffaf5]" : ""}`}>
      <div className="grid cursor-pointer grid-cols-[84px_40px_minmax(0,1fr)_minmax(0,1.6fr)_120px] items-start gap-3 px-4 py-2 text-[13px] hover:bg-[var(--bg)]"
        onClick={(e) => w.pinPop({ kind: "line", id: l.id, el: e.currentTarget })} title="Click for the product, catalog number and sources">
        <span className="mono pt-0.5">{l.id}</span>
        <span className="tnum pt-0.5 text-[var(--ink2)]">{l.qty}</span>
        <span className="pt-0.5 font-medium">{l.item}</span>
        <span className="flex flex-wrap gap-1">{tags.slice(0, 4).map((s) => <SourceTag key={s} id={s} line={l.id} />)}
          {tags.length > 4 && <span className="text-[11px] text-[var(--muted)]">+{tags.length - 4}</span>}</span>
        <span className="text-right"><span className={`badge ${st.cls}`} data-testid={`status-${l.id}`}>{st.label}</span></span>
      </div>
      {f && l.flag && <FlagCard f={f} />}
    </div>
  );
}

export function FlagCard({ f }) {
  const w = useW();
  const e = f.explanation;
  const d = w.project.decisions?.[f.id] || {};
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState("");
  const sent = ["sent", "approved"].includes(w.project.status);
  return (
    <div className="mx-4 mb-3 rounded-xl border border-[var(--line2)] bg-white p-4 text-[13px]" data-testid={`flag-${f.line}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`badge ${FLAG[f.kind].cls}`}>{FLAG[f.kind].label}</span>
        <p className="font-semibold">{e.heading}</p>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <div>
          <p className="panel-title mb-1">What the documents say</p>
          <ul className="space-y-1">{e.says.map((s, i) => <li key={i}><span className="font-medium">{s.src}:</span> <span className="text-[var(--ink2)]">{s.text}</span></li>)}</ul>
          <div className="mt-2 flex flex-wrap gap-1">{f.sources.filter((s) => !s.includes("#") || true).slice(0, 8).map((s) => <SourceTag key={s} id={s} line={f.line} />)}</div>
        </div>
        <div className="space-y-2">
          <div><p className="panel-title mb-0.5">Why it needs a decision</p><p className="text-[var(--ink2)]">{e.why}</p></div>
          <div><p className="panel-title mb-0.5">What Condoor submitted</p><p>{e.submitted}</p></div>
          <div><p className="panel-title mb-0.5">The other option</p><p className="text-[var(--ink2)]">Other option: {e.other}</p></div>
          <div><p className="panel-title mb-0.5">Who decides</p><p className="text-[var(--ink2)]">{e.decides}</p></div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[var(--line)] pt-3">
        {w.role === "supplier" ? (
          d.supplier ? (
            <>
              <span className={`badge ${d.supplier === "keep" ? "b-resolved" : "b-other"}`}>
                {d.supplier === "keep" ? (f.kind === "assumption" ? "Sent as an assumption" : "You kept Condoor's choice") : `You chose: ${f.alt_label}`}</span>
              {!sent && <button className="btn btn-ghost btn-sm" onClick={() => w.undo(f.id)} data-testid={`undo-${f.line}`}><RotateCcw size={13} /> Undo</button>}
              {d.architect && <span className={`badge ${d.architect === "accepted" ? "b-resolved" : "b-edited"}`}>Architect {d.architect}{d.architect_note ? `: ${d.architect_note}` : ""}</span>}
            </>
          ) : (
            <>
              <button className="btn btn-sm" onClick={() => w.decide(f.id, "alt")} data-testid={`alt-${f.line}`}>{f.alt_label}</button>
              <button className="btn btn-sm btn-dark" onClick={() => w.decide(f.id, "keep")} data-testid={`keep-${f.line}`}>
                {f.kind === "assumption" ? "Send as assumption" : "Keep this"}</button>
            </>
          )
        ) : (
          <>
            <span className={`badge ${d.supplier === "keep" ? "b-resolved" : "b-other"}`}>Supplier: {d.supplier === "keep" ? (f.kind === "assumption" ? "sent as an assumption" : "kept Condoor's choice") : `chose “${f.alt_label}”`}</span>
            {d.architect ? (
              <>
                <span className={`badge ${d.architect === "accepted" ? "b-resolved" : "b-edited"}`}>{d.architect === "accepted" ? (f.kind === "assumption" ? "You confirmed it" : "You accepted it") : `You edited: ${d.architect_note}`}</span>
                {!w.project.approved_at && <button className="btn btn-ghost btn-sm" onClick={() => w.review(f.id, "reopen")}><RotateCcw size={13} /> Reopen</button>}
              </>
            ) : editing ? (
              <div className="flex w-full gap-2">
                <input autoFocus value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="What should change?"
                  className="h-8 flex-1 rounded-md border border-[var(--line2)] px-2 outline-none" data-testid={`arch-note-${f.line}`} />
                <button className="btn btn-sm btn-dark" disabled={!note.trim()} onClick={async () => { await w.review(f.id, "edit", note); setEditing(false); }}>Save edit</button>
                <button className="btn btn-sm" onClick={() => setEditing(false)}>Cancel</button>
              </div>
            ) : (
              <>
                <button className="btn btn-sm" onClick={() => setEditing(true)} data-testid={`arch-edit-${f.line}`}>Edit</button>
                <button className="btn btn-sm btn-dark" onClick={() => w.review(f.id, "accept")} data-testid={`arch-accept-${f.line}`}>
                  {f.kind === "assumption" ? "Confirm assumption" : "Accept"}</button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* The issued submittal, laid out to the 08 71 00 schema: sections 1–5, then the decisions. Read-only; a line click opens its popover. */
export function SubmittalDoc() {
  const w = useW();
  const { doors, lines, checklist, items, cut_sheets: cuts, spec, abbreviations = [], mounting = [], warranties = [] } = w.data;
  const byDoor = useMemo(() => {
    const m = {};
    lines.forEach((l) => (m[l.door] = m[l.door] || []).push(l));
    return m;
  }, [lines]);
  const itemBy = Object.fromEntries(items.map((i) => [i.code, i]));
  const status = { sent: "Sent to architect", approved: "Approved", review: "Draft · not yet sent" }[w.project.status] || "Draft";
  const decided = w.data.findings.filter((f) => f.line);
  const schedule = w.data.schedule?.doc?.id || "A-601";
  const groups = [...new Set(abbreviations.map((a) => a.group))];
  return (
    <div className="mx-auto max-w-[1320px] px-6 py-6">
      <article className="paper px-10 py-9 text-[12.5px] leading-relaxed" data-testid="submittal-doc">
        <div className="flex justify-between text-[11.5px] text-[var(--muted)]">
          <span>{w.project.supplier_name} · Submittal {w.project.spec_section}-001</span><span>{status}</span>
        </div>
        <h1 className="mt-6 text-center text-[20px] font-semibold tracking-[0.08em]">DOOR HARDWARE SUBMITTAL</h1>
        <div className="mt-5 grid grid-cols-2 gap-x-10 gap-y-1">
          <p><b>Project:</b> {w.project.name}</p><p><b>Spec section:</b> Division 8 · {w.project.spec_section} Door Hardware</p>
          <p><b>Submitted by:</b> {w.project.supplier_name}</p>
          <p><b>Submitted to:</b> {w.role === "architect" ? "Architect (by invite)" : w.project.invite_email || "[ARCHITECT]"}</p>
          <p><b>Openings:</b> {doors.length} · <b>Line items:</b> {lines.length}</p><p><b>Prepared by:</b> [HARDWARE CONSULTANT NAME, SEAL]</p>
        </div>
        <div className={`mt-6 border border-[var(--line2)] ${w.outline === "contents" ? "outline-pulse" : ""}`}>
          <p className="bg-[var(--panel)] px-3 py-1.5 font-semibold">CONTENTS · as required by section {w.project.spec_section}</p>
          {checklist.map((c, i) => (
            <div key={c.key} className="flex justify-between gap-4 border-t border-[var(--line)] px-3 py-1">
              <span>{c.letter || LETTERS[i]}. {c.text}</span>
              <span className="shrink-0 text-[var(--ink2)]">{c.where}{c.status === "partial" ? " · partial" : ""}</span>
            </div>
          ))}
        </div>

        <H n="1" title="Hardware schedule by opening" k={["a", "b", "c", "d", "g"]} tags="(a)(b)(c)(d)(g)" />
        {doors.map((d) => (
          <div key={d.door} className="mt-5" data-doc-door={d.door}>
            <p className="font-semibold">Opening {d.door} · {d.room} · Hardware set {d.set}</p>
            <div className="grid grid-cols-[1fr_auto] gap-x-4 text-[var(--ink2)]">
              <p>Door: {d.width_in} × {d.height_in} in × {d.leaf_thickness_in} in, {d.leaf_material} · Frame: {d.frame_material}, {d.frame_throat_in} in throat</p>
              <span className="mono text-[var(--muted)]">(g)</span>
              <p>Label: {d.rating_min ? `${d.rating_min} min, door and frame labeled` : "none"}{d.stc ? ` · STC ${d.stc}` : ""} · Hand: {d.hand}</p><span />
              <p>Location: floor plan {d.plan_ref || "—"} · door and frame schedule {schedule}, row {d.door}</p>
              <span className="mono text-[var(--muted)]">(d)</span>
            </div>
            <div className="scroll mt-1 overflow-x-auto">
              <table className="w-full min-w-[1100px] border-collapse text-[11px]">
                <thead><tr className="text-left text-[10px] uppercase tracking-[.05em] text-[var(--muted)]">
                  {["Line", "Qty", "Item, type and style", "Function", "Size", "Label", "Hand", "Fin", "Mfr", "Cat.", "Fastening"].map((h) => <th key={h} className="py-1 pr-2 font-semibold">{h}</th>)}
                </tr></thead>
                <tbody>
                  {(byDoor[d.door] || []).map((l) => {
                    const { f, ed } = lineProduct(w, l);
                    const it = itemBy[ed?.to || l.cat] || {};
                    const open = f && l.flag && !w.project.decisions?.[f.id]?.supplier;
                    return (
                      <tr key={l.id} data-line={l.id} className={`cursor-pointer border-t border-[var(--line)] align-top hover:bg-[var(--hl)] ${w.focusLine === l.id ? "outline-pulse" : ""}`}
                        onClick={(e) => w.pinPop({ kind: "line", id: l.id, el: e.currentTarget })}>
                        <td className="mono whitespace-nowrap py-1 pr-2">{l.id}</td><td className="pr-2">{l.qty}</td>
                        <td className="pr-2">{l.item}, {ed ? ed.product : `${it.item?.split(",")[0] || ""}; ${it.style || ""}`}
                          {open ? <span className={`badge ml-1 ${FLAG[f.kind].cls}`}>[{FLAG[f.kind].label}]</span> : null}</td>
                        <td className="pr-2">{it.func}</td><td className="pr-2">{it.size}</td><td className="pr-2">{it.label}</td>
                        <td className="whitespace-nowrap pr-2">{l.hand || "—"}</td><td className="whitespace-nowrap pr-2">{String(it.finish || "").split(" ")[0]}</td>
                        <td className="pr-2">{it.mfr}</td><td className="mono whitespace-nowrap pr-2">{ed?.to || l.cat}</td><td className="min-w-[200px]">{it.fastening}</td>
                      </tr>
                    );
                  })}
                  {(w.project.arch_adds || []).filter((a) => a.door === d.door).map((a) => (
                    <tr key={a.id} className="border-t border-[var(--line)] bg-[var(--blue-bg)]"><td className="mono py-1">{a.id}</td><td>1</td><td colSpan={9}>{a.text} (added by the architect)</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        <H n="2" title="Abbreviations, symbols and codes" k={["e"]} tags="(e)" />
        <div className="mt-2 grid gap-x-8 sm:grid-cols-2">
          {groups.map((g) => (
            <div key={g} className="mt-2">
              <p className="font-semibold">{g}</p>
              {abbreviations.filter((a) => a.group === g).map((a) => <p key={a.term}><span className="mono inline-block min-w-[86px]">{a.term}</span> {a.meaning}</p>)}
            </div>
          ))}
        </div>

        <H n="3" title="Mounting locations" k={["f"]} tags="(f)" />
        <table className="mt-2 w-full text-[11.5px]"><tbody>
          {mounting.map((m) => <tr key={m.component} className="border-t border-[var(--line)]"><td className="w-[160px] py-1 font-medium">{m.label}</td><td>{m.location}</td></tr>)}
        </tbody></table>
        <p className="mt-1 font-semibold">[CONFIRM AGAINST PROJECT DRAWINGS]</p>

        <H n="4" title="Warranties" k={["h"]} tags="(h)" />
        <table className="mt-2 w-full text-[11.5px]"><tbody>
          {warranties.map((x) => <tr key={x.code} className="border-t border-[var(--line)]"><td className="mono w-[150px] py-0.5">{x.code}</td><td>{x.item}</td><td className="w-[150px]">{x.mfr}</td><td className="w-[260px]">{x.warranty}</td></tr>)}
        </tbody></table>

        <H n="5" title="Cut sheets (attached)" k={[]} />
        {Object.values(cuts).map((c) => (
          <button key={c.key} className="flex w-full justify-between border-t border-[var(--line)] py-1 text-left hover:bg-[var(--bg)]" onClick={() => w.openDoc(`cut:${c.key}`, "doc")}>
            <span>{c.file}</span><span className="text-[var(--ink2)]">{c.origin} · {c.docType.split(" · ")[1]} · {lines.filter((l) => l.cut === c.key).length} lines</span>
          </button>
        ))}

        <h2 className="mt-8 border-b border-[var(--line2)] pb-1 text-[12.5px] font-semibold tracking-wide text-[var(--ink2)]">DECISIONS AND OPEN QUESTIONS</h2>
        {decided.map((f) => {
          const d = w.project.decisions?.[f.id] || {};
          return (
            <div key={f.id} className="mt-2 border-t border-[var(--line)] pt-2">
              <p><b>{f.line}</b> · {FLAG[f.kind].label}: {f.explanation.heading}</p>
              <p className="text-[var(--ink2)]">{d.supplier === "alt" ? `Supplier chose: ${f.alt_label}.` : d.supplier ? `Submitted: ${f.explanation.submitted}` : "Waiting on the supplier."}
                {d.architect ? ` Architect: ${d.architect}${d.architect_note ? ` (${d.architect_note})` : ""}.` : ""}</p>
            </div>
          );
        })}
        <p className="mt-6 text-[11px] text-[var(--muted)]">Synthetic demonstration document; not for construction.</p>
      </article>
    </div>
  );
}

function H({ n, title, k, tags }) {
  const w = useW();
  const on = k.includes(w.outline);
  return (
    <h2 data-sec={k.join(" ")} className={`mt-8 flex justify-between border-b border-[var(--ink)] pb-1 text-[13.5px] font-semibold tracking-wide ${on ? "outline-pulse" : ""}`}>
      <span>{n}. {title.toUpperCase()}</span>{tags && <span className="mono font-normal text-[var(--muted)]">{tags}</span>}
    </h2>
  );
}
